// frontend/src/components/book/atelier/atelierLayouts.js
//
// Metadonnees statiques des formats de page proposes dans l'atelier de
// creation personnalisee — a garder synchronise avec les slugs actifs de
// layout_definitions (voir backend/sql/phase04_layout_catalog_seed.sql /
// phase08_layout_engine_v2.sql / phase11_manual_layouts.sql). L'ordre du
// tableau `slots` de chaque layout doit correspondre exactement a
// capacity.slots cote backend : c'est cet ordre qui est envoye tel quel a
// PUT /pages/:pageIndex/manual (voir BookAtelierLuxe.js), et
// manualPageBuilder.js valide dans ce meme ordre.
//
// "category" correspond aux 5 boutons proposes quand une page est vide
// (cahier des charges §3) — chaque bouton filtre simplement cette meme
// liste, jamais un second mecanisme de choix.
//
// 'title' est un slot texte a l'affichage stylise different (voir
// pageRenderer.js: renderTitleTextBlock/renderTitlePhotosBlock) mais reste
// un slot 'text' cote validation — jamais un troisieme type cote backend.

export const ATELIER_CATEGORIES = [
  { id: 'photo-single', icon: '📷' },
  { id: 'photo-multi', icon: '📷📷' },
  { id: 'photo-text', icon: '📷✍️' },
  { id: 'text', icon: '✍️' },
  { id: 'title-photos', icon: '🅃📷' },
  // phase31 (parite Exemples, 2026-10-11) : les 3 pages "sans emplacement"
  // (voir ATELIER_PAGE_KINDS plus bas) vivent dans leur PROPRE categorie —
  // elles n'ont pas de `slots`, la galerie geometrique (FormatGallery) ne
  // peut pas les representer, elles utilisent donc une galerie separee
  // (AtelierLayoutPanel.js) affichee uniquement quand cette categorie est
  // active.
  { id: 'special', icon: '📖' }
];

export const ATELIER_LAYOUTS = [
  { slug: 'FULL_PHOTO', category: 'photo-single', slots: ['photo'] },
  // Une seule photo etalee sur les DEUX pages. `spread: true` : choisir cette
  // mise en page l'applique a la paire entiere, jamais a une page seule (voir
  // BookAtelierLuxe : handleChooseLayout / l'alignement de la page jumelle).
  { slug: 'FULL_PHOTO_SPREAD', category: 'photo-single', slots: ['photo'], spread: true },
  // Les deux arrangements d'une paire de photos. Les libelles decrivent la
  // forme des CADRES (ce que l'utilisateur voit et choisit), pas le sens de
  // l'empilement : "2 photos verticales" = cote a cote, cadres etroits et
  // hauts ; "2 photos horizontales" = l'une sous l'autre, cadres larges et
  // bas. C'est le vocabulaire employe par l'utilisateur (2026-09-13), et
  // c'est le seul qui soit sans ambiguite au moment de choisir.
  { slug: 'TWO_PHOTOS', category: 'photo-multi', slots: ['photo', 'photo'] },
  { slug: 'TWO_PHOTOS_STACKED', category: 'photo-multi', slots: ['photo', 'photo'] },
  { slug: 'THREE_PHOTOS', category: 'photo-multi', slots: ['photo', 'photo', 'photo'] },
  { slug: 'FOUR_PHOTOS', category: 'photo-multi', slots: ['photo', 'photo', 'photo', 'photo'] },
  { slug: 'PHOTO_TEXT', category: 'photo-text', slots: ['photo', 'text'] },
  { slug: 'TEXT_PHOTO', category: 'photo-text', slots: ['text', 'photo'] },
  { slug: 'PHOTO_WITH_CAPTION', category: 'photo-text', slots: ['photo', 'text'] },
  // Remplace par les trois variantes ci-dessous dans la galerie (correctif 6) ; reste connue pour les pages deja faites.
  { slug: 'TWO_PHOTOS_TEXT', category: 'photo-text', slots: ['photo', 'photo', 'text'], legacy: true },
  { slug: 'TWO_PHOTOS_CAPTION_STACK', category: 'photo-text', slots: ['photo', 'photo', 'text'] },
  { slug: 'TWO_PHOTOS_CAPTION_FEATURE', category: 'photo-text', slots: ['photo', 'photo', 'text'] },
  { slug: 'TWO_PHOTOS_CAPTION_SIDE', category: 'photo-text', slots: ['photo', 'photo', 'text'] },
  { slug: 'ONE_TESTIMONY', category: 'text', slots: ['text'] },
  { slug: 'TWO_TESTIMONIES', category: 'text', slots: ['text', 'text'] },
  { slug: 'THREE_TESTIMONIES', category: 'text', slots: ['text', 'text', 'text'] },
  { slug: 'TITLE_TEXT', category: 'text', slots: ['title', 'text'] },
  { slug: 'TITLE_TWO_PHOTOS', category: 'title-photos', slots: ['title', 'photo', 'photo'] },
  { slug: 'TITLE_FOUR_PHOTOS', category: 'title-photos', slots: ['title', 'photo', 'photo', 'photo', 'photo'] },
  // --- phase31 (voir backend/sql/phase31_layout_gallery_parite_exemples.sql),
  // parite avec la galerie "Mises en page" d'Exemples, 2026-10-11 ---------
  { slug: 'SIX_PHOTOS', category: 'photo-multi', slots: ['photo', 'photo', 'photo', 'photo', 'photo', 'photo'] },
  // Un seul emplacement texte, comme ONE_TESTIMONY — le rendu dedie (fond
  // colore, grand guillemet) vit entierement cote pageRenderer.js.
  { slug: 'QUOTE', category: 'text', slots: ['text'] },
  // Meme forme que PHOTO_TEXT : la ligne de signature "ecrit par X" (livre
  // collectif) est geree au rendu, pas ici (voir renderPhotoLetterBlock).
  { slug: 'PHOTO_LETTER', category: 'photo-text', slots: ['photo', 'text'] },
  { slug: 'COLLAGE_4_PHOTOS', category: 'photo-multi', slots: ['photo', 'photo', 'photo', 'photo', 'text'] },
  // --- phase32 (voir backend/sql/phase32_photos_verticales.sql, 2026-10-16) : photos VERTICALES (telephone) ---
  { slug: 'PORTRAIT_STAGGER', category: 'photo-multi', slots: ['photo', 'photo'] },
  { slug: 'PORTRAIT_FEATURE', category: 'photo-multi', slots: ['photo', 'photo', 'photo'] },
  // --- phase35 (voir backend/sql/phase35_trois_photos_haut.sql, 2026-10-16) : 1 grande photo en haut + 2 petites ---
  { slug: 'THREE_PHOTOS_TOP', category: 'photo-multi', slots: ['photo', 'photo', 'photo'] }
];

// PHASE 8 (2026-10-16, docs/atelier-mises-en-page.html) : la galerie ne propose que DOUZE modeles, pour une page
// portrait 21 x 28 cm ou la photo verticale (3:4) est la reine. Les autres restent connus (pages deja creees,
// rendu inchange) mais ne sont plus proposes (`legacy`).
//   group    : groupe d'affichage (1, 2, 3, 4 et plus, texte, double page) ;
//   vertical : pense pour des photos VERTICALES (sert aux recommandations).
const GALLERY_META = {
  FULL_PHOTO: { group: 'one', vertical: true },
  PHOTO_WITH_CAPTION: { group: 'one', vertical: false },
  TWO_PHOTOS: { group: 'two', vertical: true },
  TWO_PHOTOS_STACKED: { group: 'two', vertical: false },
  THREE_PHOTOS_TOP: { group: 'three', vertical: false },
  PORTRAIT_FEATURE: { group: 'three', vertical: true },
  FOUR_PHOTOS: { group: 'fourPlus', vertical: true },
  COLLAGE_4_PHOTOS: { group: 'fourPlus', vertical: false },
  SIX_PHOTOS: { group: 'fourPlus', vertical: true },
  PHOTO_TEXT: { group: 'text', vertical: true },
  ONE_TESTIMONY: { group: 'text', vertical: false },
  FULL_PHOTO_SPREAD: { group: 'double', vertical: false }
};
ATELIER_LAYOUTS.forEach((layout) => {
  const meta = GALLERY_META[layout.slug];
  if (meta) Object.assign(layout, { galleryGroup: meta.group, vertical: meta.vertical });
  else layout.legacy = true;
});

// Pages "SANS EMPLACEMENT" (phase31, parite Exemples, 2026-10-11) : pas de
// `slots`, jamais passees par PUT .../manual/layoutCapacity — voir
// routes/composition.js PUT .../manual-kind et
// services/composition/pageRenderer.js (renderDedicationPage/
// renderChapterDividerManualPage/renderThankYouPage). Catalogue separe de
// ATELIER_LAYOUTS a dessein : melanger les deux aurait oblige chaque
// consommateur (FormatGallery, findAtelierLayout, availableSlugs...) a
// distinguer en permanence "a des slots" de "n'en a pas".
export const ATELIER_PAGE_KINDS = [
  { kind: 'dedication', icon: '🖋️' },
  { kind: 'chapter-divider-manuel', icon: '📖' },
  { kind: 'thank-you', icon: '💌' }
];
export const ATELIER_PAGE_KIND_VALUES = new Set(ATELIER_PAGE_KINDS.map((entry) => entry.kind));
export const getPageKindLabel = (kind, t) => t(`layouts.pageKinds.${kind}.label`);
export const getPageKindDescription = (kind, t) => t(`layouts.pageKinds.${kind}.description`, { defaultValue: '' });

export const layoutsByCategory = (categoryId) => ATELIER_LAYOUTS.filter((layout) => layout.category === categoryId);

export const findAtelierLayout = (slug) => ATELIER_LAYOUTS.find((layout) => layout.slug === slug) || null;

// Libelles traduits (chantier bilingue, 2026-09-30) : calcules A L'AFFICHAGE,
// jamais au chargement du module — sinon ils restent figes dans la langue
// active au premier import et ne suivent plus un changement de langue. Le
// meme slug/id sert de cle i18n, donc un seul point de synchronisation avec
// les JSON de locales/atelier.json.
export const getCategoryLabel = (id, t) => t(`layouts.categories.${id}`);
export const getLayoutLabel = (slug, t) => t(`layouts.slugs.${slug}`);
// Phrase courte sous le libelle dans la galerie (refonte 2026-10-11, parite
// avec la galerie "Mises en page" d'Exemples : une vignette ET une phrase,
// jamais juste un libellé seul) — meme clé i18n que getLayoutLabel, juste
// sous `descriptions` plutot que `slugs`.
export const getLayoutDescription = (slug, t) => t(`layouts.descriptions.${slug}`, { defaultValue: '' });

// Un slot 'title'/'text' accepte un item kind='texte' ; un slot 'photo'
// accepte un item kind='photo'. Utilise pour le retour visuel immediat
// (avant meme la validation serveur, qui reste la source de verite finale).
export const slotAcceptsItem = (slotType, item) => {
  if (!item) return true;
  if (slotType === 'photo') return item.kind === 'photo';
  return item.kind === 'texte';
};
